/**
 * Storyblok's Table field stores every cell as a plain string — there is no
 * richtext editor inside a cell, so editors write bullet points as lines
 * starting with "- ". HTML collapses those newlines, which renders the whole
 * cell as one run-on paragraph.
 *
 * These helpers turn that convention into real markup:
 * - a cell whose lines all start with "- " becomes a <ul>
 * - any other multi-line cell keeps its line breaks via <br>
 * - a single-line cell is returned as-is
 */

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function splitLines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

const BULLET = /^[-*•]\s+/;

/**
 * Structured view of a cell, for renderers that build JSX instead of HTML.
 */
export function parseTableCell(
  value: unknown,
): { type: "list"; items: string[] } | { type: "lines"; lines: string[] } {
  const raw = typeof value === "string" ? value : value == null ? "" : String(value);
  const lines = splitLines(raw);

  // Only treat it as a list when every line is a bullet, so a cell that merely
  // happens to start with a dash stays prose.
  if (lines.length > 0 && lines.every((line) => BULLET.test(line))) {
    return { type: "list", items: lines.map((line) => line.replace(BULLET, "")) };
  }

  return { type: "lines", lines };
}

/**
 * HTML-string view of a cell, for the richtext resolver in Article.tsx.
 * Escapes the editor-supplied text — it is interpolated into a string that
 * later goes through dangerouslySetInnerHTML.
 */
export function renderTableCell(value: unknown): string {
  const parsed = parseTableCell(value);

  if (parsed.type === "list") {
    const items = parsed.items.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
    // list-image-none overrides the prose-ul:list-image-check marker the
    // surrounding article prose applies, so cells get plain bullets.
    return `<ul class="list-disc list-image-none pl-5 my-0 text-left">${items}</ul>`;
  }

  return parsed.lines.map((line) => escapeHtml(line)).join("<br>");
}
